/**
 * Cumulative elevation gain for a single coordinate array.
 *
 * Thin wrapper kept for the existing callers (the route planner's `elevationGainString` /
 * `routesWithStats`, the map measurement tool, the embed profile). The rule itself, and its
 * descent mirror, live in `elevationTotals.ts` — see that file for why the threshold exists and
 * why descent is the same procedure rather than a separate one.
 *
 * Coordinates are `[lng, lat, elevation?]`; the elevation (index 2) is read directly.
 */
import {
  ELEVATION_THRESHOLD_M,
  altitudesFromCoordinates,
  elevationGainFromAltitudes,
} from './elevationTotals';

export function elevationGainFromCoordinates(
  coordinates: number[][],
  threshold: number = ELEVATION_THRESHOLD_M
): number {
  if (!coordinates || coordinates.length < 2) return 0;
  return elevationGainFromAltitudes(altitudesFromCoordinates(coordinates), threshold);
}

/**
 * Cumulative elevation loss (descent) for a single coordinate array.
 *
 * The mirror image of {@link elevationGainFromCoordinates}: it runs the exact
 * same moving-baseline 6m-threshold algorithm on negated elevations, so the
 * descent total is computed identically to the ascent total (and both are
 * de-noised the same way). Returns a positive number of metres descended.
 *
 * Coordinates are [lng, lat, elevation?]; the elevation (index 2) is read
 * directly, matching {@link elevationGainFromCoordinates}.
 */
export function elevationLossFromCoordinates(
  coordinates: number[][],
  threshold = 6
): number {
  if (!coordinates || coordinates.length < 2) return 0;

  const negated = coordinates.map((c) => {
    const copy = c.slice();
    copy[2] = -(c[2] ?? 0);
    return copy;
  });

  return elevationGainFromCoordinates(negated, threshold);
}
